'use client';

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Button,
  Select,
  SelectItem,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@heroui/react';
import {
  CardWrapper,
  CustomCheckbox,
  CustomHeroButton,
  FilterOption,
  ResponsiveModal,
  Typography,
} from '../../../../components';
import CustomHeroInput from '../../../../components/CustomHeroInput';
import CustomTextarea from '../../../../components/CustomTextarea';
import AlertBlock from '../../../../components/alert-block';

import { usePageBreadcrumbs } from '../../../../hooks/usePageBreadcrumbs';
import { useTranslations } from '../../../../contexts/translation.context';
import { useAuth } from '../../../../contexts/AuthContext';
import { partnerOfferService } from '../../../../api';
import partnerAccService from '../../../../api/partner/partnerAccService';
import { useNavigate, useParams } from 'react-router';
import AddAccountModal from './components/AddAccountModal';
import SuccessModal from './components/SuccessModal';
import AccountSelector from './components/AccountSelector';
import { showPartnerSuccess } from '../../../../utils/toast';
import { useFormik } from 'formik';
import { createOfferSchema, CreateOfferFormData, MAX_SUB_PLANS, OfferType } from './validation';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  CloseButton,
} from '../../../../components/icons';

// Form type that allows empty strings for number fields during editing
type CreateOfferFormValues = Omit<
  CreateOfferFormData,
  'type' | 'subs' | 'subs_pre_day' | 'subs_min' | 'subs_max' | 'active_campaigns'
> & {
  type: OfferType;
  subs: Array<{ ge: number | ''; price: number | '' }>;
  subs_pre_day: number | '';
  subs_min: number | '';
  subs_max: number | '';
  active_campaigns: number | '';
};
interface SubData {
  ge: number;
  price: number;
}

const CreateOffer: React.FC = () => {
  const { t } = useTranslations();
  const { refreshPartnerCounters } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOffer, setIsLoadingOffer] = useState(isEditMode);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string>('');
  // Тип оффера держим в отдельном стейте: useFormik не даёт обратиться
  // к formik.values внутри собственного конфига, а схема зависит от типа.
  const [offerType, setOfferType] = useState<OfferType>('cpl');

  const formik = useFormik<CreateOfferFormValues>({
    initialValues: {
      type: 'cpl',
      source: '',
      subs: [{ ge: '', price: '' }],
      subs_pre_day: '',
      subs_min: '',
      subs_max: '',
      active_campaigns: '',
      description: '',
      requirements: '',
      campaign_types: ['free'],
      trial_duration_from: '',
      trial_duration_to: '',
      show_romi: true,
      use_account_name_as_offer: false,
      other_source_name: '',
      status: 'active',
    },
    validationSchema: createOfferSchema(t, offerType),
    onSubmit: async (values) => {
      setIsLoading(true);
      setApiError('');

      // Validate OnlyFans accounts selection
      if (values.source === 'only_fans' && selectedAccounts.length === 0) {
        setApiError(t('createOffer.validationErrors.onlyfansAccountRequired'));
        setIsLoading(false);
        return;
      }

      try {
        const isSubscriptionOffer = values.type === 'subscription';

        const apiData: any = {
          type: values.type,
          source: values.source,
          // Для подписки ступень "ge" не передаётся — только цена за подписку.
          subs: values.subs.map((sub) =>
            isSubscriptionOffer ? { price: sub.price } : { ge: sub.ge, price: sub.price }
          ),
          subs_pre_day: values.subs_pre_day,
          active_campaigns: values.active_campaigns,
          description: values.description,
          requirements: values.requirements,
          campaign_types: values.campaign_types,
          trial_duration_from: values.trial_duration_from || null,
          trial_duration_to: values.trial_duration_to || null,
          show_romi: values.show_romi,
          // use_account_name_as_offer: values.use_account_name_as_offer,
          other_source_name: values.other_source_name || null,
        };

        // Блока "Всего подписчиков" нет в форме подписки — поля не отправляем вовсе.
        if (!isSubscriptionOffer) {
          apiData.subs_min = values.subs_min;
          apiData.subs_max = values.subs_max;
        }

        // status отправляем только при создании, при редактировании - не отправляем
        // (деактивация обрабатывается отдельно в handleDeactivateOffer)
        if (!isEditMode) {
          apiData.status = 'moderation';
        }

        // Only include of_models if source is only_fans
        if (values.source === 'only_fans') {
          apiData.of_models = selectedAccounts.map((id) => parseInt(id, 10));
        }

        let response;
        if (isEditMode && id) {
          response = await partnerOfferService.updateOffer(id, apiData);
        } else {
          response = await partnerOfferService.createOffer(apiData);
        }

        if (response.data) {
          // Обновляем счетчики офферов в сайдбаре
          await refreshPartnerCounters();

          if (isEditMode) {
            // При редактировании - показываем тост и редиректим
            showPartnerSuccess(t('createOffer.offerUpdatedSuccess'));
            navigate('/partner/subscribers/sales');
          } else {
            // При создании - показываем модалку с информацией о модерации
            onSuccessOpen();
          }
        }
      } catch (error: any) {
        // Показываем реальную ошибку от API или fallback сообщение
        let errorMessage = 'Произошла ошибка при сохранении оффера';

        if (error?.response?.data?.detail) {
          const detail = error.response.data.detail;
          // Если detail - массив объектов ошибок (формат FastAPI)
          if (Array.isArray(detail)) {
            errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
          }
          // Если detail - строка
          else if (typeof detail === 'string') {
            errorMessage = detail;
          }
          // Если detail - объект
          else if (typeof detail === 'object') {
            errorMessage = detail.msg || JSON.stringify(detail);
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setApiError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const formikRef = useRef(formik);

  formikRef.current = formik;
  const {
    isOpen: isAddAccountOpen,
    onOpen: onAddAccountOpen,
    onClose: onAddAccountClose,
  } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const {
    isOpen: isDeactivateOpen,
    onOpen: onDeactivateOpen,
    onClose: onDeactivateClose,
  } = useDisclosure();

  const [isDeactivating, setIsDeactivating] = useState(false);
  const loadOfferData = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoadingOffer(true);
      const response = await partnerOfferService.getOfferById(id);
      const offerData = response.data;

      if (offerData) {
        // Офферы, созданные до появления поля type, считаем CPL.
        const loadedType: OfferType = offerData.type === 'subscription' ? 'subscription' : 'cpl';
        setOfferType(loadedType);

        formikRef.current.setValues({
          type: loadedType,
          source: offerData.source,
          subs: offerData.subs.map((sub: any) => ({
            ge: sub.ge ?? '',
            price: typeof sub.price === 'string' ? parseFloat(sub.price) : sub.price,
          })),
          subs_pre_day: offerData.subs_pre_day,
          // Для подписки бэкенд не возвращает subs_min/subs_max — иначе инпуты
          // станут uncontrolled и React выдаст warning.
          subs_min: offerData.subs_min ?? '',
          subs_max: offerData.subs_max ?? '',
          active_campaigns: offerData.active_campaigns,
          description: offerData.description,
          requirements: offerData.requirements,
          campaign_types: offerData.campaign_types,
          trial_duration_from: offerData.trial_duration_from || '',
          trial_duration_to: offerData.trial_duration_to || '',
          show_romi: offerData.show_romi,
          use_account_name_as_offer: offerData.use_account_name_as_offer || false,
          other_source_name: offerData.other_source_name || '',
          status: offerData.status,
          // //@ts-ignore
          // subscribe_days: offerData.trial_duration_from || '',
          // expire_days: offerData.trial_duration_to || ''
        });
        // Set selected accounts from offer data (converting to strings for Select component)
        const accountsFromOffer = (offerData.of_models || []).map((accId: number) => String(accId));
        setSelectedAccounts(accountsFromOffer);
      }
    } catch (error) {
      console.error('Error loading offer data:', error);
      // Navigate back to offers list on error
      navigate('/partner/subscribers/sales');
    } finally {
      setIsLoadingOffer(false);
    }
  }, [id, navigate]);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const fetchAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await partnerAccService.getAccounts();
      const accountsData = response.data?.objects || [];
      setAccounts(accountsData);
    } catch (error) {
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Load offer data on component mount for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadOfferData();
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (formik.values.source === 'only_fans') {
      fetchAccounts();
    } else {
      // Clear selected accounts when source is not only_fans
      setSelectedAccounts([]);
    }
  }, [formik.values.source]);

  const handleAccountCreated = () => {
    fetchAccounts();
  };

  const breadcrumbs = useMemo(
    () => [
      { label: t('salesFans.home'), href: '/' },
      { label: t('salesFans.salesFans'), href: '/partner/subscribers/sales' },
      { label: isEditMode ? t('createOffer.editOffer') : t('createOffer.title'), isActive: true },
    ],
    [t, isEditMode]
  );

  usePageBreadcrumbs(breadcrumbs);
  const getProposalSources = (t: any): FilterOption[] => [
    // { key: 'all', label: t('proposals.allSources'), icon: React.createElement(GlobalIc, { size: 16 }) },
    {
      key: 'only_fans',
      label: t('proposals.onlyFans'),
      icon: React.createElement(OnlyFansIc, { size: 16, fill: '#A8A8B0' }),
    },
    {
      key: 'reddit',
      label: t('proposals.reddit'),
      icon: React.createElement(IcReddit, { size: 16 }),
    },
    {
      key: 'instagram',
      label: t('proposals.instagram'),
      icon: React.createElement(InstagramIc, { size: 16, fill: '#A8A8B0' }),
    },
    {
      key: 'tiktok',
      label: t('proposals.tiktok'),
      icon: React.createElement(IcTikTok, { size: 16 }),
    },
    {
      key: 'google',
      label: t('proposals.google'),
      icon: React.createElement(GoogleIc, { size: 16, fill: '#A8A8B0' }),
    },
    {
      key: 'telegram',
      label: t('proposals.telegram'),
      icon: React.createElement(IcTg, { size: 16 }),
    },
    {
      key: 'twitter',
      label: t('proposals.twitter'),
      icon: React.createElement(TwitterIc, { size: 16, fill: '#A8A8B0' }),
    },
    {
      key: 'youtube',
      label: t('proposals.youtube'),
      icon: React.createElement(IcYoutube, { size: 16 }),
    },
    {
      key: 'another',
      label: t('proposals.otherOne'),
      icon: React.createElement(IcAnotherSoc, { size: 16 }),
    },
  ];
  const sourceOptions = getProposalSources(t);

  const handleInputChange = (field: keyof CreateOfferFormValues, value: any) => {
    if (field === 'source' && typeof value === 'object' && value !== null) {
      value = value.currentKey || value.anchorKey || value;
    }

    // Смена типа оффера меняет набор полей — сбрасываем зависимые значения
    // и снимаем ошибки от полей, которых на экране уже нет.
    if (field === 'type') {
      const nextType: OfferType = value === 'subscription' ? 'subscription' : 'cpl';
      setOfferType(nextType);

      if (nextType === 'subscription') {
        formik.setFieldValue('subs', [{ ge: '', price: '' }]);
        formik.setFieldValue('subs_min', '');
        formik.setFieldValue('subs_max', '');
      }
      formik.setFieldValue('subs_pre_day', '');
      formik.setFieldValue('type', nextType);
      // shouldValidate = false, иначе валидация тут же вернёт снятые ошибки
      formik.setTouched({}, false);
      formik.setErrors({});
      return;
    }

    // Handle number fields - convert empty string or NaN to empty string for proper validation
    if (['subs_pre_day', 'subs_min', 'subs_max', 'active_campaigns'].includes(field)) {
      const numValue =
        typeof value === 'string'
          ? field === 'subs_pre_day' || field === 'active_campaigns'
            ? parseInt(value)
            : parseInt(value)
          : value;
      value = isNaN(numValue) || value === '' ? '' : numValue;
    }

    formik.setFieldValue(field, value);
    // Trigger validation for the field after setting value
    setTimeout(() => {
      formik.validateField(field);
    }, 0);
  };

  const addSubPlan = () => {
    if ((formik.values.subs || []).length >= MAX_SUB_PLANS) {
      return;
    }
    formik.setFieldValue('subs', [...(formik.values.subs || []), { ge: '', price: '' }]);
  };

  const removeSubPlan = (index: number) => {
    formik.setFieldValue(
      'subs',
      (formik.values.subs || []).filter((_, i) => i !== index)
    );
  };

  const updateSubPlan = (index: number, field: 'ge' | 'price', value: string) => {
    const numValue = field === 'price' ? parseFloat(value) : parseInt(value);
    const finalValue = isNaN(numValue) ? '' : numValue;

    const updatedSubs = (formik.values.subs || []).map((sub, i) =>
      i === index ? { ...sub, [field]: finalValue } : sub
    );
    formik.setFieldValue('subs', updatedSubs);
  };

  const isSubLimitReached = (formik.values.subs || []).length >= MAX_SUB_PLANS;
  const isSubscription = formik.values.type === 'subscription';

  const handleCancel = () => {
    navigate('/partner/subscribers/sales');
  };

  const handleDeactivateOffer = async () => {
    if (!id) return;

    try {
      setIsDeactivating(true);
      await partnerOfferService.updateOffer(id, { status: 'hidden' });

      // Обновляем счетчики офферов в сайдбаре
      await refreshPartnerCounters();

      onDeactivateClose();
      navigate('/partner/subscribers/sales');
    } catch (error) {
      console.error('Error deactivating offer:', error);
      setApiError(t('createOffer.deactivateError') || 'Ошибка при деактивации оффера');
    } finally {
      setIsDeactivating(false);
    }
  };

  if (isLoadingOffer) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="px-4 md:px-6 py-4">
          <Typography variant="h5" className="font-semibold text-auth-ink text-2xl">
            {isEditMode ? t('createOffer.editOffer') : t('createOffer.newOffer')}
          </Typography>
        </div>
        <div className="px-4 md:px-6 py-4 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-partner mx-auto mb-4"></div>
            <Typography variant="body2" className="text-zinc-500">
              {t('createOffer.loadingOffer')}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <Typography variant="h5" className="font-semibold text-auth-ink text-2xl">
          {isEditMode ? t('createOffer.editOffer') : t('createOffer.newOffer')}
        </Typography>
        {isEditMode &&
          formik.values?.status !== 'hidden' &&
          formik.values?.status !== 'moderation' && (
            <CustomHeroButton
              variant="primary"
              onPress={onDeactivateOpen}
              className="px-4 py-2 h-10 bg-partner text-white rounded-xl"
              title={t('createOffer.deactivateOffer') || 'Деактивировать оффер'}
            />
          )}
      </div>

      <div className="px-4 md:px-6 py-4 flex-1">
        <div className="w-full">
          <CardWrapper>
            <form
              ref={formRef}
              onSubmit={formik.handleSubmit}
              className="flex flex-col gap-4 bg-white"
            >
              <div className="max-w-[37.5rem]">
                <div className="flex items-center mb-2">
                  <Typography variant="body1" className="font-normal text-sm text-auth-ink">
                    {t('createOffer.offerType')}
                    <span className="text-partner">*</span>
                  </Typography>
                </div>
                <Select
                  selectedKeys={[formik.values.type]}
                  isDisabled={isEditMode}
                  onSelectionChange={(key) => {
                    const selectedValue = typeof key === 'string' ? key : Array.from(key)[0];
                    if (!selectedValue) return;
                    handleInputChange('type', selectedValue as any);
                  }}
                  placeholder={t('createOffer.selectOfferType')}
                  aria-label={t('createOffer.offerType')}
                  classNames={{
                    trigger:
                      'h-[2.375rem] bg-white border border-[0.125rem] rounded-lg shadow-sm border-zinc-300',
                    label: '!mb-2',
                    value: 'text-sm text-zinc-500',
                  }}
                >
                  <SelectItem key="cpl" textValue={t('createOffer.offerTypeCpl')}>
                    {t('createOffer.offerTypeCpl')}
                  </SelectItem>
                  <SelectItem key="subscription" textValue={t('createOffer.offerTypeSubscription')}>
                    {t('createOffer.offerTypeSubscription')}
                  </SelectItem>
                </Select>
              </div>

              <div className="max-w-[37.5rem]">
                <div className="flex items-center mb-2">
                  <Typography
                    variant="body1"
                    className={`font-normal text-sm ${
                      formik.touched.source && formik.errors.source
                        ? 'text-auth-strength-weak'
                        : 'text-auth-ink'
                    }`}
                  >
                    {t('createOffer.source')}
                    <span
                      className={
                        formik.touched.source && formik.errors.source
                          ? 'text-auth-strength-weak'
                          : 'text-partner'
                      }
                    >
                      *
                    </span>
                  </Typography>
                </div>
                <Select
                  selectedKeys={[formik.values.source]}
                  onSelectionChange={(key) => {
                    const selectedValue = typeof key === 'string' ? key : Array.from(key)[0];
                    handleInputChange('source', selectedValue as any);
                  }}
                  onClose={() => formik.setFieldTouched('source', true)}
                  placeholder={t('createOffer.selectSource')}
                  aria-label={t('createOffer.source')}
                  renderValue={() => {
                    const selectedOption = sourceOptions.find(
                      (opt) => opt.key === formik.values.source
                    );
                    if (!selectedOption) return t('createOffer.selectSource');

                    return (
                      <div className="flex items-center gap-2">
                        {selectedOption.icon && (
                          <span className="flex-shrink-0">{selectedOption.icon}</span>
                        )}
                        <span>{selectedOption.label}</span>
                      </div>
                    );
                  }}
                  classNames={{
                    trigger: `h-[2.375rem] bg-white border border-[0.125rem] rounded-lg shadow-sm ${
                      formik.touched.source && formik.errors.source
                        ? 'border-auth-strength-weak'
                        : 'border-zinc-300'
                    }`,
                    label: '!mb-2',
                    value: 'text-sm text-zinc-500',
                  }}
                >
                  {sourceOptions.map((option) => (
                    <SelectItem key={option.key} textValue={option.label}>
                      <div className="flex items-center gap-2">
                        {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </Select>
                {formik.touched.source && formik.errors.source && (
                  <Typography variant="body2" className="text-auth-strength-weak mt-1 text-xs">
                    {String(formik.errors.source)}
                  </Typography>
                )}
              </div>

              {formik.values.source === 'another' && (
                <div className="max-w-[37.5rem] mt-2">
                  {/* @ts-ignore */}
                  <CustomHeroInput
                    type="text"
                    value={formik.values.other_source_name}
                    onChange={(e) => handleInputChange('other_source_name', e.target.value)}
                    onBlur={formik.handleBlur}
                    placeholder={t('createOffer.enterSourceName')}
                    error={
                      formik.touched.other_source_name && formik.errors.other_source_name
                        ? String(formik.errors.other_source_name)
                        : undefined
                    }
                    touched={formik.touched.other_source_name && !!formik.errors.other_source_name}
                    required
                    className="!mt-[-1.5rem]"
                  />
                </div>
              )}

              {formik.values.source === 'only_fans' && (
                <div className="max-w-[37.5rem] flex flex-col h-full">
                  <AccountSelector
                    accounts={accounts}
                    selectedAccounts={selectedAccounts}
                    onSelectionChange={setSelectedAccounts}
                    isLoading={isLoadingAccounts}
                    onAddAccount={onAddAccountOpen}
                    useAccountNameAsOffer={formik.values.use_account_name_as_offer}
                    onUseAccountNameChange={(checked) =>
                      handleInputChange('use_account_name_as_offer', checked)
                    }
                    // error={formik.touched.of_models && formik.errors.of_models ? String(formik.errors.of_models) : undefined}
                    // touched={formik.touched.of_models}
                    // onClose={() => formik.setFieldTouched('of_models', true)}
                  />
                </div>
              )}

              <div className="space-y-4">
                {(formik.values.subs || []).map((sub, index) => (
                  <div
                    key={index}
                    className={`flex gap-2 ${(formik.values.subs || []).length > 1 && index > 0 && '!mt-[-1rem]'}`}
                  >
                    <div className="flex gap-2 w-full max-w-[37.5rem]">
                      {!isSubscription && (
                        <>
                          <div className="flex-1">
                            <CustomHeroInput
                              label={index === 0 ? t('createOffer.subscriberPrice') : ''}
                              type="number"
                              value={sub.ge === '' ? '' : sub.ge.toString()}
                              onChange={(e) => updateSubPlan(index, 'ge', e.target.value)}
                              onBlur={formik.handleBlur}
                              placeholder={'≥100'}
                              error={
                                formik.touched.subs?.[index]?.ge &&
                                typeof formik.errors.subs?.[index] === 'object' &&
                                formik.errors.subs?.[index] &&
                                'ge' in formik.errors.subs[index]
                                  ? String((formik.errors.subs[index] as any).ge)
                                  : undefined
                              }
                              touched={formik.touched.subs?.[index]?.ge}
                              required
                              className={index === 0 ? '' : '!mt-[1.125rem]'}
                            />
                          </div>
                          <div className="flex items-center justify-center mt-6 w-2 h-10 text-zinc-500 max-md:self-end">
                            =
                          </div>
                        </>
                      )}
                      <div className={`flex-1 ${isSubscription ? '' : 'max-md:self-end'}`}>
                        <CustomHeroInput
                          label={isSubscription ? t('createOffer.subscriptionPrice') : ''}
                          type="number"
                          step="any"
                          value={sub.price === '' ? '' : sub.price.toString()}
                          onChange={(e) => updateSubPlan(index, 'price', e.target.value)}
                          onBlur={formik.handleBlur}
                          placeholder={
                            isSubscription
                              ? t('createOffer.subscriptionPricePlaceholder')
                              : t('createOffer.pricePlaceholder')
                          }
                          error={
                            formik.touched.subs?.[index]?.price &&
                            typeof formik.errors.subs?.[index] === 'object' &&
                            formik.errors.subs?.[index] &&
                            'price' in formik.errors.subs[index]
                              ? String((formik.errors.subs[index] as any).price)
                              : undefined
                          }
                          touched={formik.touched.subs?.[index]?.price}
                          startContent="$"
                          required
                          className={isSubscription ? '' : '!mt-[1.125rem]'}
                        />
                      </div>
                    </div>
                    {!isSubscription && (formik.values.subs || []).length > 1 && index > 0 && (
                      <div className="!max-w-10">
                        <Button
                          size="sm"
                          className="h-[2.375rem] !mt-[1.625rem] !px-0 !max-w-10 !min-w-10 !w-10 bg-auth-surface rounded-2"
                          onPress={() => removeSubPlan(index)}
                        >
                          <CloseButton />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                {/* <div className="text-sm text-zinc-500 max-w-[37.5rem]">
                  <p>{t('createOffer.offerPriceIs')} <span className="text-auth-ink font-medium">$0</span></p>
                  <p>{t('createOffer.offerBelowAverage')}</p>
                </div> */}
                {!isSubscription && (
                  <>
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      className={`h-8 px-3 text-xs bg-[#e6f1fe] text-partner rounded-lg ${isSubLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onPress={addSubPlan}
                      isDisabled={isSubLimitReached}
                    >
                      {t('createOffer.addPrice')}
                    </Button>
                    {isSubLimitReached && (
                      <Typography variant="body2" className="text-xs text-zinc-500 mt-1">
                        {t('createOffer.maxSubPlansLimit', { count: MAX_SUB_PLANS })}
                      </Typography>
                    )}
                  </>
                )}
              </div>

              <div className="max-w-[37.5rem]">
                <CustomHeroInput
                  label={
                    isSubscription
                      ? t('createOffer.fansPerMonthMin')
                      : t('createOffer.fansPerDayMin')
                  }
                  type="number"
                  value={(formik.values.subs_pre_day || '').toString()}
                  onChange={(e) => handleInputChange('subs_pre_day', e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder={isSubscription ? '~100' : '≥100'}
                  error={
                    formik.touched.subs_pre_day && formik.errors.subs_pre_day
                      ? String(formik.errors.subs_pre_day)
                      : undefined
                  }
                  touched={formik.touched.subs_pre_day && !!formik.errors.subs_pre_day}
                  required
                  infoIconText={
                    isSubscription
                      ? t('createOffer.fansPerMonthMin')
                      : t('createOffer.fansPerDayMin')
                  }
                />
                <Typography variant="body2" className="text-zinc-500 mt-1 text-xs">
                  {isSubscription
                    ? t('createOffer.approxFansPerMonthDescription')
                    : t('createOffer.minFansPerDayDescription')}
                </Typography>
              </div>

              {!isSubscription && (
                <div className="max-w-[37.5rem]">
                  <div className="flex  gap-2">
                    <div className="flex-1">
                      <CustomHeroInput
                        label={t('createOffer.totalSubscribers')}
                        type="number"
                        value={(formik.values.subs_min || '').toString()}
                        onChange={(e) => handleInputChange('subs_min', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder={t('createOffer.maxPlaceholder')}
                        error={
                          formik.touched.subs_min && formik.errors.subs_min
                            ? String(formik.errors.subs_min)
                            : undefined
                        }
                        touched={formik.touched.subs_min && !!formik.errors.subs_min}
                        required
                      />
                    </div>
                    <div className="flex items-center !mt-6 justify-center w-2 h-10 text-zinc-500">
                      =
                    </div>
                    <div className="flex-1">
                      <CustomHeroInput
                        label=""
                        type="number"
                        value={(formik.values.subs_max || '').toString()}
                        onChange={(e) => handleInputChange('subs_max', e.target.value)}
                        onBlur={formik.handleBlur}
                        placeholder={t('createOffer.maxPlaceholderInfinity')}
                        error={
                          formik.touched.subs_max && formik.errors.subs_max
                            ? String(formik.errors.subs_max)
                            : undefined
                        }
                        touched={formik.touched.subs_max && !!formik.errors.subs_max}
                        required
                        className="!mt-[1.125rem]"
                      />
                    </div>
                  </div>
                  <Typography variant="body2" className="text-zinc-500 mt-1 text-xs">
                    {t('createOffer.totalSubscribersDescription')}
                  </Typography>
                </div>
              )}

              {!isEditMode && (
                <div className="max-w-[37.5rem]">
                  <CustomHeroInput
                    label={t('createOffer.activeCampaigns')}
                    type="number"
                    value={(formik.values.active_campaigns || '').toString()}
                    onChange={(e) => handleInputChange('active_campaigns', e.target.value)}
                    onBlur={formik.handleBlur}
                    placeholder={t('createOffer.campaignsPlaceholder')}
                    error={
                      formik.touched.active_campaigns && formik.errors.active_campaigns
                        ? String(formik.errors.active_campaigns)
                        : undefined
                    }
                    touched={formik.touched.active_campaigns && !!formik.errors.active_campaigns}
                    required
                  />
                  <Typography variant="body2" className="text-zinc-500 mt-1 text-xs">
                    {t('createOffer.activeCampaignsDescription')}
                  </Typography>
                </div>
              )}

              <div className="max-w-[37.5rem]">
                <CustomTextarea
                  label={t('createOffer.description')}
                  value={formik.values.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('description', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  placeholder={t('createOffer.describeSourcePlaceholder')}
                  minRows={7}
                  maxRows={8}
                  error={
                    formik.touched.description && formik.errors.description
                      ? String(formik.errors.description)
                      : undefined
                  }
                  touched={formik.touched.description && !!formik.errors.description}
                />
                <Typography variant="body2" className="text-zinc-500 mt-1 text-xs">
                  {t('createOffer.describeSourceDescription')}
                </Typography>
              </div>

              <div className="max-w-[37.5rem]">
                <CustomTextarea
                  label={t('createOffer.requirements')}
                  value={formik.values.requirements}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('requirements', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                  placeholder={t('createOffer.describeRequirementsPlaceholder')}
                  minRows={7}
                  maxRows={8}
                  error={
                    formik.touched.requirements && formik.errors.requirements
                      ? String(formik.errors.requirements)
                      : undefined
                  }
                  touched={formik.touched.requirements && !!formik.errors.requirements}
                  required
                />
                <Typography variant="body2" className="text-zinc-500 mt-1 text-xs">
                  {t('createOffer.describeRequirementsDescription')}
                </Typography>
              </div>

              <div className="max-w-[50rem]">
                <Typography
                  variant="body1"
                  className={`font-normal text-sm mb-2 ${
                    formik.touched.campaign_types && formik.errors.campaign_types
                      ? 'text-auth-strength-weak'
                      : 'text-auth-ink'
                  }`}
                >
                  {t('createOffer.campaignTypes')}
                  <span
                    className={
                      formik.touched.campaign_types && formik.errors.campaign_types
                        ? 'text-auth-strength-weak'
                        : 'text-partner'
                    }
                  >
                    *
                  </span>
                </Typography>
                <div className="flex items-center gap-4 gap-y-2 flex-wrap">
                  {[
                    { key: 'free', label: t('createOffer.freeAccountByLink') },
                    // { key: 'paid', label: t('createOffer.paidAccountByLink') },
                    { key: 'trial', label: t('createOffer.paidAccountFreeTrial') },
                  ].map((option) => (
                    <div>
                      <CustomCheckbox
                        key={option.key}
                        size="md"
                        isSelected={(formik.values.campaign_types || []).includes(option.key)}
                        onValueChange={(checked) => {
                          formik.setFieldTouched('campaign_types', true);
                          if (checked) {
                            handleInputChange('campaign_types', [
                              ...(formik.values.campaign_types || []),
                              option.key,
                            ]);
                          } else {
                            handleInputChange(
                              'campaign_types',
                              (formik.values.campaign_types || []).filter((t) => t !== option.key)
                            );
                            // Clear trial duration fields when unchecking trial
                            if (option.key === 'trial') {
                              formik.setFieldValue('trial_duration_from', '');
                              formik.setFieldValue('trial_duration_to', '');
                            }
                          }
                        }}
                        className="text-sm text-black"
                      >
                        {option.label}
                      </CustomCheckbox>
                    </div>
                  ))}
                </div>
                {formik.touched.campaign_types && formik.errors.campaign_types && (
                  <Typography variant="body2" className="text-auth-strength-weak mt-1 text-xs">
                    {String(formik.errors.campaign_types)}
                  </Typography>
                )}
              </div>

              {(formik.values.campaign_types || []).includes('trial') && (
                <div className="max-w-[37.5rem]">
                  <Typography variant="body1" className="font-normal text-auth-ink text-sm mb-2">
                    {t('createOffer.trialDurationLimit')}
                  </Typography>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Select
                        selectedKeys={
                          formik.values.trial_duration_from
                            ? [String(formik.values.trial_duration_from)]
                            : []
                        }
                        onSelectionChange={(key) => {
                          const selectedValue = typeof key === 'string' ? key : Array.from(key)[0];
                          handleInputChange('trial_duration_from', selectedValue as any);
                          if (
                            formik.values.trial_duration_to &&
                            Number(formik.values.trial_duration_to) < Number(selectedValue)
                          ) {
                            formik.setFieldValue('trial_duration_to', selectedValue);
                          }
                        }}
                        onClose={() => formik.setFieldTouched('trial_duration_from', true)}
                        placeholder={`${t('createOffer.trialFrom')}: ${t('createOffer.duration7Days').toLowerCase()}`}
                        aria-label={t('createOffer.trialFrom')}
                        classNames={{
                          trigger: `h-[2.375rem] bg-white border border-[0.125rem] rounded-lg shadow-sm ${
                            formik.touched.trial_duration_from && formik.errors.trial_duration_from
                              ? 'border-auth-strength-weak'
                              : 'border-zinc-300'
                          }`,
                          value: 'text-sm text-zinc-500',
                          label: '!mb-2',
                        }}
                      >
                        {[
                          { key: '1', label: t('createOffer.duration1Day') },
                          { key: '3', label: t('createOffer.duration3Days') },
                          { key: '7', label: t('createOffer.duration7Days') },
                          { key: '14', label: t('createOffer.duration14Days') },
                          { key: '30', label: t('createOffer.duration1Month') },
                          { key: '90', label: t('createOffer.duration3Months') },
                          { key: '180', label: t('createOffer.duration6Months') },
                        ].map((option) => {
                          const toValue = formik.values.trial_duration_to;
                          const isDisabled = toValue ? Number(option.key) > Number(toValue) : false;
                          return (
                            <SelectItem
                              key={option.key}
                              textValue={option.label}
                              isDisabled={isDisabled}
                            >
                              {option.label}
                            </SelectItem>
                          );
                        })}
                      </Select>
                      {formik.touched.trial_duration_from && formik.errors.trial_duration_from && (
                        <Typography
                          variant="body2"
                          className="text-auth-strength-weak mt-1 text-xs"
                        >
                          {String(formik.errors.trial_duration_from)}
                        </Typography>
                      )}
                    </div>
                    <div className="flex-1">
                      <Select
                        selectedKeys={
                          formik.values.trial_duration_to
                            ? [String(formik.values.trial_duration_to)]
                            : []
                        }
                        onSelectionChange={(key) => {
                          const selectedValue = typeof key === 'string' ? key : Array.from(key)[0];
                          if (
                            formik.values.trial_duration_from &&
                            Number(selectedValue) < Number(formik.values.trial_duration_from)
                          ) {
                            return;
                          }
                          handleInputChange('trial_duration_to', selectedValue as any);
                        }}
                        onClose={() => formik.setFieldTouched('trial_duration_to', true)}
                        placeholder={`${t('createOffer.trialTo')}: ${t('createOffer.duration3Months').toLowerCase()}`}
                        aria-label={t('createOffer.trialTo')}
                        classNames={{
                          trigger: `h-[2.375rem] bg-white border border-[0.125rem] rounded-lg shadow-sm ${
                            formik.touched.trial_duration_to && formik.errors.trial_duration_to
                              ? 'border-auth-strength-weak'
                              : 'border-zinc-300'
                          }`,
                          value: 'text-sm text-zinc-500',
                          label: '!mb-2',
                        }}
                      >
                        {[
                          { key: '1', label: t('createOffer.duration1Day') },
                          { key: '3', label: t('createOffer.duration3Days') },
                          { key: '7', label: t('createOffer.duration7Days') },
                          { key: '14', label: t('createOffer.duration14Days') },
                          { key: '30', label: t('createOffer.duration1Month') },
                          { key: '90', label: t('createOffer.duration3Months') },
                          { key: '180', label: t('createOffer.duration6Months') },
                        ].map((option) => {
                          const fromValue = formik.values.trial_duration_from;
                          const isDisabled = fromValue
                            ? Number(option.key) < Number(fromValue)
                            : false;
                          return (
                            <SelectItem
                              key={option.key}
                              textValue={option.label}
                              isDisabled={isDisabled}
                            >
                              {option.label}
                            </SelectItem>
                          );
                        })}
                      </Select>
                      {formik.touched.trial_duration_to && formik.errors.trial_duration_to && (
                        <Typography
                          variant="body2"
                          className="text-auth-strength-weak mt-1 text-xs"
                        >
                          {String(formik.errors.trial_duration_to)}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="max-w-[37.5rem]">
                <Typography variant="body1" className="font-normal text-auth-ink text-sm mb-2">
                  ROMI
                </Typography>
                <CustomCheckbox
                  isSelected={formik.values.show_romi}
                  size="md"
                  onValueChange={(checked) => handleInputChange('show_romi', checked)}
                  className="text-sm text-black"
                >
                  {t('createOffer.makeRomiPublic')}
                </CustomCheckbox>
              </div>
            </form>
          </CardWrapper>

          {/* Отображение ошибок валидации */}
          {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
            <div className="mt-6">
              <AlertBlock
                data={Object.entries(formik.errors)
                  .filter(
                    ([field]) =>
                      ![
                        'type',
                        'trial_duration_from',
                        'trial_duration_to',
                        'source',
                        'subs',
                        'subs_pre_day',
                        'subs_min',
                        'subs_max',
                        'active_campaigns',
                        'description',
                        'requirements',
                        'campaign_types',
                        'other_source_name',
                      ].includes(field)
                  )
                  .map(([field, error]) => ({
                    severity: 'error' as const,
                    description:
                      typeof error === 'string'
                        ? error
                        : typeof error === 'object' && error !== null
                          ? JSON.stringify(error)
                          : String(error),
                  }))}
                type="rounded"
              />
            </div>
          )}

          {/* Отображение ошибки API */}
          {apiError && typeof apiError === 'string' && (
            <div className="mt-6">
              <AlertBlock
                data={[
                  {
                    severity: 'error' as const,
                    description: apiError,
                  },
                ]}
                type="rounded"
              />
            </div>
          )}

          {/* {formik.values.source === 'only_fans' && !selectedAccount && (
            <div className="mt-6">
              <AlertBlock
                data={[{
                  severity: "error" as const,
                  description: t('createOffer.validationErrors.onlyfansAccountRequired')
                }]}
                type="rounded"
              />
            </div>
          )}
           */}
          <div className="flex flex-col mt-8 gap-4">
            <AlertBlock
              className="md:max-w-[68.5625rem]"
              data={[
                {
                  severity: 'info',
                  title: t('createOffer.moderationTime'),
                  descriptions: [t('createOffer.moderationTimeDescription')],
                },
              ]}
            />

            <div className="flex gap-2">
              <CustomHeroButton
                variant="tertiary"
                onPress={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 h-10 bg-zinc-100 text-auth-ink border-none rounded-xl"
                title={t('createOffer.back')}
              />

              <CustomHeroButton
                onPress={() => {
                  formik.handleSubmit();
                }}
                isLoading={isLoading}
                disabled={isLoading}
                className="px-4 py-2 h-10 bg-partner text-white rounded-xl"
                title={
                  isLoading
                    ? isEditMode
                      ? t('createOffer.updating')
                      : t('createOffer.creating')
                    : isEditMode
                      ? t('createOffer.update')
                      : t('createOffer.save')
                }
              />
            </div>
          </div>
        </div>
      </div>

      <AddAccountModal
        isOpen={isAddAccountOpen}
        onClose={onAddAccountClose}
        onSuccess={handleAccountCreated}
      />

      <SuccessModal isOpen={isSuccessOpen} onClose={onSuccessClose} />

      {/* Deactivate Offer Modal */}
      <ResponsiveModal isOpen={isDeactivateOpen} onClose={onDeactivateClose} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <Typography variant="h6" className="text-auth-ink">
              {t('createOffer.deactivateOfferTitle') || 'Деактивировать оффер'}
            </Typography>
          </ModalHeader>
          <ModalBody>
            <Typography variant="body2" className="text-auth-muted">
              {t('createOffer.deactivateOfferDescription') ||
                'Вы уверены, что хотите деактивировать этот оффер? После деактивации он станет недоступен для клиентов.'}
            </Typography>
          </ModalBody>
          <ModalFooter>
            <CustomHeroButton
              variant="tertiary"
              onPress={onDeactivateClose}
              disabled={isDeactivating}
              className="px-4 py-2 h-10 !bg-auth-strength-weak/20 !font-normal !text-auth-strength-weak border-none rounded-xl"
              title={t('createOffer.cancel') || 'Отмена'}
            />
            <CustomHeroButton
              onPress={handleDeactivateOffer}
              isLoading={isDeactivating}
              disabled={isDeactivating}
              className="px-4 py-2 h-10 bg-auth-strength-weak text-white rounded-xl"
              title={
                isDeactivating
                  ? t('createOffer.deactivating') || 'Деактивация...'
                  : t('createOffer.deactivate') || 'Деактивировать'
              }
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
};

export default CreateOffer;
